<template>
    <Head title="Create Incident Type" />

    <div class="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <!-- Back Button -->
        <div class="mb-4">
            <Link
                :href="route('settings.incidents.types.index')"
                class="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Incident Types
            </Link>
        </div>

        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center gap-3">
                    <div class="flex-1">
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Create New Incident Type</h2>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Define a new incident type with default severity and priority settings.</p>
                    </div>
                    <InfoButton title="Creating Incident Types" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            The Create Incident Type page allows you to define new classification categories for your
                            incident management system. Each incident type you create serves as a template that guides
                            incident reporting, establishes default response parameters, and enables consistent handling
                            of similar incidents across your organization. Well-designed incident types streamline the
                            reporting process, ensure appropriate SLA application, and facilitate meaningful trend
                            analysis and regulatory reporting.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Name:</strong> Enter a clear, descriptive name that users will easily recognize
                              (e.g., "Slip, Trip, or Fall", "Equipment Malfunction", "Data Breach")<br>
                            • <strong>Category:</strong> Select the broad classification (Safety, Health, Environmental,
                              Security, Operational, Quality, Transportation, Compliance, Workplace, Contractor, Visitor,
                              General)<br>
                            • <strong>Description:</strong> Provide detailed information about what this incident type
                              covers, when it should be used, and any specific reporting requirements<br>
                            • <strong>Default Severity:</strong> Set the typical severity level (Low, Medium, High,
                              Critical) - can be adjusted during triage<br>
                            • <strong>Default Priority:</strong> Set the typical response urgency (Low, Normal, High,
                              Urgent) - can be adjusted during triage<br>
                            • <strong>Active Status:</strong> Check to make this type immediately available in incident
                              reporting forms, or leave unchecked to save as inactive<br>
                            • <strong>SLA Response Time:</strong> Optional hours for initial response requirement<br>
                            • <strong>SLA Investigation Time:</strong> Optional hours for investigation completion if
                              triage determines investigation is needed<br>
                            • Click <strong>Create Incident Type</strong> to save, or <strong>Cancel</strong> to discard
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Use user-friendly names</strong> - Avoid technical jargon that reporters might not
                              understand<br>
                            • <strong>Write clear descriptions</strong> - Help users distinguish between similar incident
                              types<br>
                            • <strong>Consider your audience</strong> - Types should match the language and understanding
                              of those reporting incidents<br>
                            • <strong>Set appropriate defaults</strong> - Default severity and priority guide users but
                              don't lock them in<br>
                            • <strong>Be specific with categories</strong> - Proper categorization enables better filtering,
                              analysis, and reporting<br>
                            • <strong>Think about SLAs</strong> - Response and investigation timeframes should be realistic
                              given your team's capacity<br>
                            • <strong>Start with common incidents</strong> - Create types for incidents you actually
                              experience, not hypothetical scenarios<br>
                            • <strong>Plan for growth</strong> - You can always add more types later as needs emerge<br>
                            • <strong>Align with regulations</strong> - If you have industry-specific reporting requirements,
                              ensure types match those standards<br>
                            • New incident types become available immediately in the incident reporting form once marked as
                              active
                        </p>
                    </InfoButton>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6 space-y-6">
                <!-- Basic Information -->
                <div class="space-y-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Basic Information</h3>

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Name <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <input
                                id="name"
                                v-model="form.name"
                                type="text"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                :class="{ 'border-red-300 dark:border-red-500': form.errors.name }"
                                placeholder="e.g., Slip, Trip, or Fall"
                            />
                            <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                        </div>

                        <div>
                            <label for="category" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Category <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <select
                                id="category"
                                v-model="form.category"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="">Select Category</option>
                                <option value="Safety">Safety</option>
                                <option value="Health">Health</option>
                                <option value="Environmental">Environmental</option>
                                <option value="Security">Security</option>
                                <option value="Operational">Operational</option>
                                <option value="Quality">Quality</option>
                                <option value="Transportation">Transportation</option>
                                <option value="Compliance">Compliance</option>
                                <option value="Workplace">Workplace</option>
                                <option value="Contractor">Contractor</option>
                                <option value="Visitor">Visitor</option>
                                <option value="Property">Property</option>
                                <option value="General">General</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            placeholder="Brief description of this incident type..."
                        ></textarea>
                    </div>

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label for="icon" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Icon
                            </label>
                            <select
                                id="icon"
                                v-model="form.icon"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="">Select Icon</option>
                                <option value="alert-triangle">⚠️ Alert Triangle</option>
                                <option value="alert-octagon">🛑 Alert Octagon</option>
                                <option value="alert-circle">⚠️ Alert Circle</option>
                                <option value="flame">🔥 Flame</option>
                                <option value="droplet">💧 Droplet</option>
                                <option value="zap">⚡ Zap</option>
                                <option value="tool">🔧 Tool</option>
                                <option value="heart">❤️ Heart</option>
                                <option value="shield-off">🛡️ Shield</option>
                                <option value="lock">🔒 Lock</option>
                                <option value="truck">🚚 Truck</option>
                                <option value="package">📦 Package</option>
                                <option value="box">📦 Box</option>
                                <option value="users">👥 Users</option>
                                <option value="user-check">✓ User Check</option>
                                <option value="home">🏠 Home</option>
                                <option value="briefcase">💼 Briefcase</option>
                                <option value="file-text">📄 File Text</option>
                                <option value="clipboard">📋 Clipboard</option>
                                <option value="cloud">☁️ Cloud</option>
                                <option value="database">💾 Database</option>
                                <option value="activity">📊 Activity</option>
                                <option value="target">🎯 Target</option>
                                <option value="check-circle">✅ Check Circle</option>
                                <option value="trash-2">🗑️ Trash</option>
                            </select>
                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Visual representation for this incident type</p>
                        </div>

                        <div>
                            <label for="color" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Color Theme
                            </label>
                            <select
                                id="color"
                                v-model="form.color"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="">Select Color</option>
                                <option value="red">🔴 Red</option>
                                <option value="orange">🟠 Orange</option>
                                <option value="yellow">🟡 Yellow</option>
                                <option value="green">🟢 Green</option>
                                <option value="blue">🔵 Blue</option>
                                <option value="indigo">🟣 Indigo</option>
                                <option value="purple">🟣 Purple</option>
                                <option value="pink">🩷 Pink</option>
                                <option value="gray">⚪ Gray</option>
                                <option value="cyan">🔷 Cyan</option>
                                <option value="teal">💚 Teal</option>
                                <option value="amber">🟡 Amber</option>
                            </select>
                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Background color theme for the icon badge</p>
                        </div>
                    </div>
                </div>

                <!-- Severity & Priority -->
                <div class="space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Default Severity & Priority</h3>

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label for="default_severity" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Default Severity <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <select
                                id="default_severity"
                                v-model="form.default_severity"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>
                        </div>

                        <div>
                            <label for="default_priority" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Default Priority
                            </label>
                            <select
                                id="default_priority"
                                v-model="form.default_priority"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Settings -->
                <div class="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Status</h3>

                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="is_active"
                                v-model="form.is_active"
                                type="checkbox"
                                class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                            />
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="is_active" class="font-medium text-gray-700 dark:text-gray-300">Active</label>
                            <p class="text-gray-500 dark:text-gray-400">Make this incident type available for selection when reporting incidents</p>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Link
                        :href="route('settings.incidents.types.index')"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        {{ form.processing ? 'Creating...' : 'Create Incident Type' }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

// Initialize form with useSecureForm for company context protection
const form = useSecureForm({
    name: '',
    description: '',
    category: '',
    icon: '',
    color: '',
    default_severity: 'medium',
    default_priority: 'normal',
    requires_investigation: true, // Always set to true (default), not editable in UI
    is_active: true,
});

const submit = () => {
    form.post(route('settings.incidents.types.store'), {
        preserveScroll: true,
    });
};
</script>
